/**
 * Single source of truth for chart & canvas colors.
 *
 * Chart.js and the OG-image canvas render outside the DOM and therefore
 * cannot read CSS custom properties. These literals MUST stay in sync with
 * the brand tokens in `src/styles/tokens.css` / `globals.css`.
 *
 * Do NOT hardcode chart colors anywhere else — import from here.
 */

export const CHART_COLORS = {
  primary: "#10b981",      // --primary  (emerald)
  secondary: "#6366f1",    // --secondary (indigo)
  accentOrange: "#f59e0b", // --accent-orange (amber)
  danger: "#ef4444",       // --danger (red)
  accentCyan: "#22d3ee",   // --accent-cyan
  violet: "#8b5cf6",       // --violet
  blue: "#3b82f6",         // categorical-only (charts may use extra hues)
  cyanDeep: "#06b6d4",     // categorical-only
  grey: "#64748b",         // neutral chart segment
  slate: "#475569",        // neutral chart segment (darker)
  pink: "#ec4899",         // categorical-only
} as const;

/**
 * Ordered categorical series — mirrors --chart-1..--chart-6 in tokens.css.
 * Multi-series charts assign colors in this order when no semantic color
 * is requested.
 */
export const CHART_SERIES = [
  CHART_COLORS.primary,
  CHART_COLORS.secondary,
  CHART_COLORS.accentOrange,
  CHART_COLORS.danger,
  CHART_COLORS.accentCyan,
  CHART_COLORS.violet,
] as const;

/** Derive a translucent fill from a palette hex (keeps alpha variants DRY). */
export function chartColorWithAlpha(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Theme-aware chart chrome (axis text, grid lines, tooltip surfaces). */
export const CHART_THEME = {
  light: {
    text: "#475569",
    grid: "rgba(15, 23, 42, 0.05)",
    tooltipBg: "#ffffff",
    tooltipTitle: "#0f172a",
    muted: "#64748b",
    tooltipBorder: "rgba(15, 23, 42, 0.1)",
    fallbackFill: "rgba(15, 23, 42, 0.05)",
    fallbackBorder: "rgba(15, 23, 42, 0.08)",
  },
  dark: {
    text: "#94a3b8",
    grid: "rgba(255, 255, 255, 0.05)",
    tooltipBg: "#131a2c",
    tooltipTitle: "#f8fafc",
    muted: "#64748b",
    tooltipBorder: "rgba(255, 255, 255, 0.1)",
    fallbackFill: "rgba(255, 255, 255, 0.05)",
    fallbackBorder: "rgba(255, 255, 255, 0.08)",
  },
} as const;

/** Translucent fills used for area charts / point rings. */
export const CHART_FILLS = {
  primarySoft: "rgba(16, 185, 129, 0.05)",
  primaryMed: "rgba(16, 185, 129, 0.15)",
  dangerSoft: "rgba(239, 68, 68, 0.05)",
  secondarySoft: "rgba(99, 102, 241, 0.05)",
  blueSoft: "rgba(59, 130, 246, 0.1)",   // categorical blue area fill
  neutralSoft: "rgba(255, 255, 255, 0.05)", // neutral series fill (canvas)
  onAccent: "#ffffff",
} as const;
